import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import PreviewModeBanner from "./PreviewModeBanner.jsx";
import { currentPreview } from "./previewMode.js";
import AdminApp from "./AdminApp.jsx";
import ProfilePage from "./ProfilePage.jsx";
import WinnersPage from "./WinnersPage.jsx";
import ReplayPage from "./ReplayPage.jsx";
import ContentPage from "./ContentPage.jsx";
import LegalDocPage from "./LegalDocPage.jsx";
import ResetPasswordPage from "./ResetPasswordPage.jsx";
import UnsubscribePage from "./UnsubscribePage.jsx";
import InstallPage from "./InstallPage.jsx";
import DemoCompsReplayPage from "./DemoCompsReplayPage.jsx";
import SiteHeader from "./SiteHeader.jsx";
import { NavigationContext } from "./Navigation.jsx";
import { wsRequest } from "./wsClient.js";
import { CurrencyProvider } from "./Currency.jsx";
import { ThemeProvider } from "./ThemeContext.jsx";
import ToastContainer from "./ToastContainer.jsx";
import "./theme.css";
import "./requiredFieldStyling.js";

// Whole-app client-side router. Top-level pages are just 3 (/, /admin,
// /profile), but each now has its own nested routes for individual tabs
// (e.g. /admin/competitions/stats, /profile/payments) — parsed by each page
// component itself from the `path` prop passed down here. This file just
// tracks the current path, listens for back/forward (popstate), and
// exposes a navigate(path) function via context that updates history and
// state together — no full page reload for any in-app link.
function Root() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Scrolling to top needs to happen after the new page has actually
  // rendered, not before — doing it synchronously inside navigate() (as a
  // previous version did) ran ahead of React's state update, so the OLD
  // page's content would snap to the top first, and only a moment later
  // would the new page's content swap in — two separate visual shifts
  // instead of one clean transition, which is what was actually causing
  // the reported "jump" on every in-app navigation.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  const navigate = (to) => {
    if (to !== window.location.pathname) {
      window.history.pushState({}, "", to);
    }
    setPath(to);
  };

  // Until a root account exists, this site is not usable: no settings, no
  // competitions, nothing to show. The installer tells the user to finish
  // at /install, but nothing stopped them navigating away and landing on
  // an empty homepage that looks broken rather than unfinished.
  //
  // Fails the SAFE way: if the check errors we assume installed and carry
  // on, because a transient WebSocket failure must never lock everyone out
  // of a working site. InstallPage fails in the same direction for the
  // mirror-image reason - it never shows the setup form on an unclear
  // check.
  const [needsInstall, setNeedsInstall] = useState(null);
  useEffect(() => {
    wsRequest("install.status", {})
      .then((d) => setNeedsInstall(!!d.needsInstall))
      .catch(() => setNeedsInstall(false));
  }, []);
  useEffect(() => {
    // replaceState, not pushState: Back must not be an escape route from
    // an install that has to happen.
    // /legal IS EXEMPT, like /embed. The install page's own footer links to
    // Terms, Disclaimers and Privacy — offering links that bounce straight
    // back is worse than not offering them, and somebody deciding whether
    // to run this software may reasonably want to read the terms before
    // creating an account. Reported 7 Sep.
    if (
      needsInstall &&
      !path.startsWith("/install") &&
      !path.startsWith("/embed") &&
      !path.startsWith("/legal")
    ) {
      window.history.replaceState({}, "", "/install");
      setPath("/install");
    }
  }, [needsInstall, path]);

  // Render nothing until the answer is known, or the homepage flashes up
  // for a moment before being replaced - which is exactly the confusing
  // half-state this is meant to remove.
  if (needsInstall === null) return null;

  // THE MODE FOR THE WHOLE SITE, not just this route.
  //
  // A token in the path starts or replaces a preview session; otherwise
  // whatever this tab is already in continues. That is what makes the
  // mode follow the visitor to pages carrying no token of their own —
  // My Account being the one that mattered: it used to drop silently
  // back to Live, and the entry-credit top-up asked for live payment
  // credentials on a site that was supposed to be in test.
  const preview = currentPreview(path);
  const previewToken = preview ? preview.token : null;

  let page = <App demoRouteToken={previewToken} />;
  if (path.startsWith("/admin")) page = <AdminApp path={path} />;
  else if (path.startsWith("/profile")) page = <ProfilePage path={path} demoRouteToken={previewToken} />;
  // THE PUBLIC WINNERS LIST. Above /page/ because it is a route of its
  // own rather than a content page an operator wrote.
  // THE PUBLIC REPLAY VIEWER. Above /winners only because both are this
  // site's own routes rather than operator-written pages; the order
  // between them does not matter, but /replay/ must be matched before
  // the /page/ catch-all below.
  else if (path.startsWith("/replay/")) {
    const id = path.slice("/replay/".length).split("/")[0];
    page = <ReplayPage competitionId={id} demoRouteToken={previewToken} />;
  }
  else if (path.startsWith("/winners"))
    // THE PREVIEW TOKEN, like every other page gets. Without it the
    // Winners page always asked as "live", so a test-route visitor saw
    // an empty list while their test competition sat there ended.
    page = <WinnersPage demoRouteToken={previewToken} />;
  else if (path.startsWith("/page/")) {
    const slug = path.slice("/page/".length).split("/")[0];
    page = <ContentPage slug={slug} />;
  }
  else if (path.startsWith("/legal/")) {
    const docKey = path.slice("/legal/".length).split("/")[0];
    page = <LegalDocPage docKey={docKey} />;
  }
  else if (path.startsWith("/reset-password")) page = <ResetPasswordPage />;
  else if (path.startsWith("/unsubscribe")) page = <UnsubscribePage />;
  else if (path.startsWith("/install")) page = <InstallPage />;
  else if (path.startsWith("/demo-comps/")) {
    const token = path.slice("/demo-comps/".length).split("/")[0];
    page = <DemoCompsReplayPage token={token} />;
  }
  else if (path.startsWith("/test-route-")) {
    // The TEST preview. currentPreview above has already recorded this
    // token for the tab, so App is given it the same way every other
    // page now is — this branch exists to select App rather than to
    // extract the token.
    page = <App demoRouteToken={previewToken} />;
  }
  else if (path.startsWith("/demo-route-")) {
    // Same as the test branch above: currentPreview has already taken
    // the token out of the path and recorded it for this tab. See
    // setSimulationRunning on the backend for how it is
    // generated/invalidated.
    page = <App demoRouteToken={previewToken} />;
  }

  return (
    <NavigationContext.Provider value={navigate}>
      {/* Ships with the session-wide mode and is not optional — an
          invisible mode with no way out is worse than the inconsistency
          it replaces. The padding is applied here because this is the
          only place that knows whether the banner is on screen. */}
      <PreviewModeBanner preview={preview} />
      <div style={preview ? { paddingTop: 34 } : undefined}>
      <ThemeProvider>
        <CurrencyProvider>
          {!path.startsWith("/admin") && !path.startsWith("/embed") && !needsInstall && <SiteHeader />}
          {page}
          <ToastContainer />
        </CurrencyProvider>
      </ThemeProvider>
      </div>
    </NavigationContext.Provider>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<Root />);
